import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Localization } from '@freelancer/localization';
import { Location } from '@freelancer/location';
import {
  SEO_CONFIG,
  Seo,
  SeoConfig,
  StructuredDataClassName,
} from '@freelancer/seo';
import { Assets } from '@freelancer/ui/assets';
import { ContainerSize } from '@freelancer/ui/container';
import type { Observable } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-home-page',
  template: `
    <fl-tracking-hotjar-init></fl-tracking-hotjar-init>
    <app-home-page-hero></app-home-page-hero>

    <app-invitation-redirect-alert></app-invitation-redirect-alert>

    <div
      flTrackingSection="HomePage"
      class="Container"
    >
      <fl-container
        [mobilePadding]="true"
        [size]="ContainerSize.DESKTOP_LARGE"
        [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
      >
        <app-companies-trust-logo
          class="mb-mid tablet:mb-none"
          [isRedesign]="true"
        ></app-companies-trust-logo>
      </fl-container>
      <fl-container
        [size]="ContainerSize.FLUID"
        [padding]="false"
      >
        <app-home-page-make-it-real
          class="SectionFeature mb-mid desktop_large:mb-none"
        ></app-home-page-make-it-real>
      </fl-container>
      <app-home-page-discover-section
        class="mb-xxlarge tablet:mb-none desktop_large:mb-small"
        [galleryContentIdentifier]="'top'"
      >
      </app-home-page-discover-section>
      <fl-container
        [size]="ContainerSize.FLUID"
        [padding]="false"
      >
        <app-home-page-global-talent
          class="SectionFeature"
        ></app-home-page-global-talent>
      </fl-container>
      <fl-container
        [mobilePadding]="true"
        [size]="ContainerSize.DESKTOP_LARGE"
      >
        <app-as-seen-on> </app-as-seen-on>
      </fl-container>
      <app-home-page-discover-section
        class="Discover mb-xxlarge desktop:mb-xxxxlarge"
        [galleryContentIdentifier]="'middle'"
        [reverseGallery]="true"
      >
      </app-home-page-discover-section>
      <fl-container
        class="HireCategoriesContainer"
        [size]="ContainerSize.DESKTOP_LARGE"
        [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
      >
        <app-home-page-hire-categories
          class="HireCategories mb-xxlarge tablet:mb-xxxxlarge"
        ></app-home-page-hire-categories>
      </fl-container>
      @if (!isDomainIndia) {
        <fl-container
          [size]="ContainerSize.DESKTOP_XLARGE"
          [mobilePadding]="true"
        >
          <app-home-page-ai-services></app-home-page-ai-services>
        </fl-container>
      }
      <app-home-page-discover-section
        class="mb-xxlarge tablet:mb-none"
        [galleryContentIdentifier]="'bottom'"
      >
      </app-home-page-discover-section>
      <fl-container
        [size]="ContainerSize.FLUID"
        [padding]="false"
      >
        <app-home-page-api-enterprise-v5
          class="SectionFeature"
        ></app-home-page-api-enterprise-v5>
      </fl-container>
    </div>
  `,
  styleUrls: ['./home-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageComponent implements OnInit {
  private assets = inject(Assets);
  private localization = inject(Localization);
  private location = inject(Location);
  private seo = inject(Seo);
  private seoConfig = inject<SeoConfig>(SEO_CONFIG);

  ContainerSize = ContainerSize;

  showSeoNavbar: boolean;

  isHeroTest$: Observable<boolean | undefined>;
  isDomainIndia = false;

  ngOnInit(): void {
    this.isDomainIndia =
      (this.location.hostname.match(/freelancer.in/) || []).length > 0;

    this.showSeoNavbar = this.localization.isEnglish();

    this.seo.insertStructuredData(
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Freelancer',
        logo: this.assets.getUrl(this.seoConfig.defaultMetaImage),
        url: 'https://www.freelancer.com',
        sameAs: [
          'https://twitter.com/freelancer',
          'https://www.facebook.com/fansoffreelancer',
          'https://www.linkedin.com/company/freelancer-com',
          'https://www.instagram.com/freelancerofficial',
        ],
      },
      StructuredDataClassName.ORGANIZATION_SHCEMA,
    );

    this.seo.insertStructuredData(
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Freelancer',
        url: this.location.origin,
      },
      StructuredDataClassName.WEBSITE_SCHEMA,
    );
  }
}
